import { Injectable } from '@angular/core';
import {
  AccountApi,
  AccountApiModule,
  AccountApiResource,
  AccountApiResourceResponse
} from '@limehome/account-api-client';
import { AuthService } from '../../authentication/services/auth.service';
import { catchError, map, Observable, throwError } from 'rxjs';
import { UserAccount, UserProfile } from '../interfaces/customer.interface';
import {
  AccountApiError,
  AccountApiResponse,
  AccountReservation,
  ChangePasswordPayload,
  ClaimBooking,
  CustomerReservation,
  SignUpPayload
} from '../interfaces/account-api.interface';

/**
 * To use `AccountApiService`, you need to ensure
 * `AccountApiClient` is provided at App Module under providers array.
 * Provide the following provider [Click here](../config/account-api-client-provider.config.ts)
 */
@Injectable({
  providedIn: 'root'
})
export class AccountApiService {
  publicModule: AccountApiModule;
  customerResource: AccountApiResource<UserAccount>;

  constructor(
    private readonly accountApi: AccountApi,
    private readonly authService: AuthService
  ) {
    this.accountApi.usePublicToken(this.authService.authToken);
    this.setModuleAndResource();
  }

  /**
   * Initializes the API module and resource for customer operations.
   */
  setModuleAndResource() {
    try {
      this.publicModule = this.accountApi.module('public');
      this.customerResource = this.publicModule.resource('customer');

      if (!this.publicModule || !this.customerResource) {
        throw new Error('Failed to initialize API module or resource.');
      }
    } catch (error) {
      console.error('Error initializing AuthService:', error);
    }
  }

  /**
   * Retrieves customer account details.
   * @param {string} token - The authentication token for the customer.
   * @returns {Observable<AccountApiResourceResponse<UserAccount>>} - An observable that emits the customer account details.
   */
  getCustomer(token: string): Observable<AccountApiResourceResponse<UserAccount>> {
    this.accountApi.usePublicToken(token);
    return this.publicModule.resource('customer').get() as Observable<AccountApiResourceResponse<UserAccount>>;
  }

  /**
   * Sign-up a user.
   * @param {SignUpPayload} data - The data required for signing up.
   * @returns {Observable<UserAccount>} - An observable that emits the newly created customer account.
   */
  signUp(data: SignUpPayload): Observable<UserAccount> {
    const { profile, email, password, newsletter, source } = data;
    const userProfile = profile || {};

    return this.customerResource
      .call('signup', {
        email,
        password,
        newsletter,
        source,
        profile: userProfile
      })
      .pipe(
        map((response: AccountApiResponse<UserAccount>): UserAccount => response.payload),
        catchError((err: AccountApiError) => {
          return throwError(() => err);
        })
      );
  }

  /**
   * Activates a user account.
   * @param {string} token - The activation token.
   * @returns {Observable<AccountApiResourceResponse<UserAccount>>} - An observable that emits the activated customer account.
   */
  activateCustomer(token: string): Observable<AccountApiResourceResponse<UserAccount>> {
    return this.customerResource.call('activate', {
      token
    });
  }

  /**
   * Requests a password reset for the user.
   * @param {string} email - The email of the customer.
   * @param {string} recaptcha - The reCAPTCHA response token.
   * @returns {Observable<AccountApiResourceResponse<UserAccount>>} - An observable that emits the result of the password reset request.
   */
  resetPasswordRequest(email: string, recaptcha: string): Observable<AccountApiResourceResponse<UserAccount>> {
    const reqOption = recaptcha ? { headers: { 'Recaptcha-Response': recaptcha } } : {};
    this.accountApi.usePublicToken(this.authService.authToken);
    return this.customerResource.call('request-password-reset', { email }, reqOption);
  }

  /**
   * Sends a request to reset the user password.
   * @param {string} password - The new password.
   * @param {string} token - The password reset token.
   * @returns {Observable<AccountApiResourceResponse<UserAccount>>} - An observable that emits the result of the password reset request.
   */
  resetPassword(password: string, token: string): Observable<AccountApiResourceResponse<UserAccount>> {
    this.accountApi.usePublicToken(this.authService.authToken);
    return this.customerResource.call('reset-password', { password, token });
  }

  /**
   * Updates the customer profile with the provided partial profile data.
   * @param {Partial<Profile>} data - The partial profile data to be updated.
   * @returns {Observable<AccountApiResourceResponse<Profile>>} - An observable that emits the updated profile data.
   */
  updateCustomer(data: Partial<UserProfile>): Observable<AccountApiResourceResponse<UserProfile>> {
    try {
      this.accountApi.usePublicToken(this.authService.authToken);
      const profileResource = this.publicModule.resource('customer/profile');
      const request$ = profileResource.update(data) as Observable<AccountApiResourceResponse<UserProfile>>;
      return request$ as Observable<AccountApiResourceResponse<UserProfile>>;
    } catch (error) {
      return throwError(() => error);
    }
  }

  /**
   * Sets the password for the customer.
   * @param {ChangePasswordPayload} data - The data containing the new password details.
   * @returns {Observable<AccountApiResourceResponse<UserAccount>>} - An observable that emits the result of the password update.
   */
  setPassword(data: ChangePasswordPayload): Observable<AccountApiResourceResponse<UserAccount>> {
    return this.customerResource.call('update-password', data);
  }

  /**
   * Claims a booking for the customer.
   * @param {ClaimBooking} data - The data required to claim the booking.
   * @returns {Observable<AccountApiResourceResponse<AccountReservation>>} - An observable that emits the result of the booking claim.
   */
  claimBooking(data: ClaimBooking): Observable<AccountApiResourceResponse<AccountReservation>> {
    const customerResource = this.publicModule.resource('customer/reservations');
    return customerResource.call('claim', data);
  }

  /**
   * Verifies the reset password token.
   * @param {string} token - The password reset token.
   * @returns {Observable<AccountApiResourceResponse<null>>} - An observable that emits the result of the token validation.
   */
  verifyResetPasswordToken(token: string) {
    return this.customerResource.call<any, AccountApiResourceResponse<null>>('validate-password-reset-token', {
      token
    });
  }

  /**
   * Retrieves customer reservations.
   * @param {string} [queryParams] - Optional query parameters for filtering reservations.
   * @returns {Observable<AccountApiResourceResponse<CustomerReservation[]>>} - An observable that emits the customer's reservations.
   */
  getReservations(queryParams?: string): Observable<AccountApiResourceResponse<CustomerReservation[]>> {
    const customerResource = this.publicModule.resource(
      queryParams ? 'customer/reservations?' + queryParams : 'customer/reservations'
    );

    return customerResource.get() as Observable<AccountApiResourceResponse<CustomerReservation[]>>;
  }

  /**
   * Retrieves details of a specific reservation.
   * @param {string} id - The ID of the reservation.
   * @returns {Observable<AccountApiResourceResponse<CustomerReservation>>} - An observable that emits the reservation details.
   */
  getReservationDetails(id: string): Observable<AccountApiResourceResponse<CustomerReservation>> {
    const customerResource = this.publicModule.resource(`customer/reservations/${id}`);
    return customerResource.get() as Observable<AccountApiResourceResponse<CustomerReservation>>;
  }
}
